import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuthContext } from '../components/AuthProvider';
import { useLanguage } from '../context/LanguageContext';
import { getLoginLandingPath } from '../utils/modulePermissions';

/**
 * `returnTo` comes from the URL, so only an in-app path is honoured: it must
 * start with exactly one '/' (no protocol-relative '//host', no '/\host'
 * which browsers normalise to '//host', no scheme such as 'javascript:'),
 * contain no control characters, and still resolve to this origin.
 */
function safeReturnToPath(raw: string | null | undefined): string | null {
  if (!raw || typeof raw !== 'string') return null;
  if (!raw.startsWith('/') || raw.startsWith('//') || raw.startsWith('/\\')) return null;
  // eslint-disable-next-line no-control-regex
  if (/[\u0000-\u001f\u007f\\]/.test(raw)) return null;
  try {
    const origin = window.location.origin;
    const resolved = new URL(raw, origin);
    if (resolved.origin !== origin) return null;
    return `${resolved.pathname}${resolved.search}${resolved.hash}`;
  } catch {
    return null;
  }
}

export function LoginPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { login, error, errorInfo, user } = useAuthContext();
  const { t } = useLanguage();
  const [redirectAfterLogin, setRedirectAfterLogin] = useState(false);

  const [step, setStep] = useState<1 | 2>(1);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [slowLogin, setSlowLogin] = useState(false);
  const [retryAfterCountdown, setRetryAfterCountdown] = useState(0);

  const reason = searchParams.get('reason');
  const isRateLimited = retryAfterCountdown > 0;
  const showSlowLoginNotice = loading && slowLogin;
  const retryAfterLabel = useMemo(() => {
    if (!retryAfterCountdown) return '';
    const minutes = Math.floor(retryAfterCountdown / 60);
    const seconds = retryAfterCountdown % 60;
    if (minutes > 0) {
      return `${minutes}:${String(seconds).padStart(2, '0')}`;
    }
    return `${seconds}s`;
  }, [retryAfterCountdown]);

  useEffect(() => {
    if (!loading) {
      setSlowLogin(false);
      return;
    }
    const timeoutId = window.setTimeout(() => {
      setSlowLogin(true);
    }, 4000);
    return () => window.clearTimeout(timeoutId);
  }, [loading]);

  useEffect(() => {
    if (!errorInfo?.retryAfterSeconds || errorInfo.retryAfterSeconds <= 0) {
      return;
    }
    setRetryAfterCountdown(errorInfo.retryAfterSeconds);
  }, [errorInfo]);

  useEffect(() => {
    if (retryAfterCountdown <= 0) return;
    const timeoutId = window.setTimeout(() => {
      setRetryAfterCountdown((current) => Math.max(current - 1, 0));
    }, 1000);
    return () => window.clearTimeout(timeoutId);
  }, [retryAfterCountdown]);

  function handleContinue(e: React.FormEvent) {
    e.preventDefault();
    if (step === 1) {
      setStep(2);
      return;
    }
    void handleLogin();
  }

  // The login response already carries the full user (stored by login()),
  // so the landing page is resolved from it — no extra /auth/me round trip.
  useEffect(() => {
    if (!redirectAfterLogin || !user) return;
    setRedirectAfterLogin(false);
    navigate(getLoginLandingPath(user, safeReturnToPath(searchParams.get('returnTo'))), { replace: true });
  }, [redirectAfterLogin, user, navigate, searchParams]);

  async function handleLogin() {
    if (isRateLimited) return;
    setLoading(true);
    const result = await login(email, password);
    if (result.success) {
      setRedirectAfterLogin(true);
    }
    setLoading(false);
  }

  const statusCardStyle = isRateLimited
    ? { background: 'rgba(239,68,68,0.12)', border: '1px solid rgba(248,113,113,0.28)', color: '#FECACA' }
    : showSlowLoginNotice
    ? { background: 'rgba(249,115,22,0.12)', border: '1px solid rgba(251,146,60,0.24)', color: '#FDBA74' }
    : null;

  const helperCopy = isRateLimited
    ? t('login.status.rateLimited', { value: retryAfterLabel })
    : showSlowLoginNotice
    ? t('login.status.slow')
    : errorInfo?.isTimeout
    ? t('login.status.timeout')
    : errorInfo?.isNetworkError
    ? t('login.status.network')
    : null;

  return (
    <div
      className="min-h-screen flex overflow-hidden relative"
      style={{ background: '#07131C', fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif" }}
    >
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          backgroundImage:
            'repeating-linear-gradient(0deg,transparent 0 39px,rgba(255,255,255,0.03) 39px 40px),' +
            'repeating-linear-gradient(90deg,transparent 0 39px,rgba(255,255,255,0.03) 39px 40px)',
        }}
      />

      <div
        className="absolute pointer-events-none"
        style={{
          width: '56%',
          height: '340px',
          background: 'rgba(249,115,22,0.07)',
          top: '50%',
          left: '-8%',
          transform: 'translateY(-50%) rotate(-14deg)',
          transformOrigin: 'center',
          filter: 'blur(2px)',
        }}
      />

      <div className="relative z-10 flex flex-col justify-between p-10 lg:p-14 w-full lg:w-1/2">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 flex-shrink-0">
            <svg viewBox="0 0 200 200" xmlns="http://www.w3.org/2000/svg">
              <g transform="translate(100 100) rotate(45)">
                <rect x="-86" y="-22" width="172" height="44" rx="4" fill="#F97316" />
                <circle cx="-66" cy="-10" r="3" fill="#0D1F2D" opacity="0.6" />
                <circle cx="-66" cy="10" r="3" fill="#0D1F2D" opacity="0.6" />
                <circle cx="66" cy="-10" r="3" fill="#0D1F2D" opacity="0.6" />
                <circle cx="66" cy="10" r="3" fill="#0D1F2D" opacity="0.6" />
              </g>
              <g transform="translate(100 100) rotate(-45)">
                <rect x="-86" y="-22" width="172" height="44" rx="4" fill="#F97316" />
                <circle cx="-66" cy="-10" r="3" fill="#0D1F2D" opacity="0.6" />
                <circle cx="-66" cy="10" r="3" fill="#0D1F2D" opacity="0.6" />
                <circle cx="66" cy="-10" r="3" fill="#0D1F2D" opacity="0.6" />
                <circle cx="66" cy="10" r="3" fill="#0D1F2D" opacity="0.6" />
              </g>
              <circle cx="100" cy="100" r="6" fill="#0D1F2D" />
              <circle cx="100" cy="100" r="2.5" fill="#F97316" />
            </svg>
          </div>
          <span className="text-white font-bold text-xl tracking-[0.14em]">
            FOR<span style={{ color: '#F97316' }}>X</span>A
          </span>
        </div>

        <div className="mt-auto mb-auto pt-16 lg:pt-0 max-w-lg">
          <div className="flex items-center gap-2 mb-6">
            <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ background: '#F97316' }} />
            <span className="text-xs font-semibold tracking-[0.22em] uppercase" style={{ color: 'rgba(255,255,255,0.4)' }}>
              {t('login.welcomeBack')}
            </span>
          </div>

          <h1 className="font-bold leading-[1.05] mb-6" style={{ fontSize: 'clamp(2.6rem, 5vw, 4.2rem)', color: '#F1F5F9' }}>
            {t('login.titlePrefix')}
            <br />
            <span style={{ color: '#F97316' }}>{t('login.titleAccent')}</span>
          </h1>

          <p className="text-base leading-relaxed max-w-sm" style={{ color: 'rgba(255,255,255,0.4)' }}>
            {t('login.subtitle')}
          </p>
        </div>

        <p className="hidden lg:block text-[11px] font-semibold tracking-[0.2em] uppercase mt-12" style={{ color: 'rgba(255,255,255,0.18)' }}>
          {t('login.tagline')}
        </p>
      </div>

      <div className="relative z-10 hidden lg:flex items-center justify-center p-10 lg:p-14 w-1/2">
        <div
          className="w-full max-w-md rounded-2xl p-10"
          style={{ background: 'rgba(13,31,45,0.85)', border: '1px solid #1B3142', backdropFilter: 'blur(12px)' }}
        >
          <div className="flex items-center gap-1.5 mb-8">
            <span className="text-xs font-bold tracking-[0.2em] uppercase" style={{ color: '#F97316' }}>
              {'// '}{t('login.step')} {step === 1 ? '01' : '02'}
            </span>
            <span className="text-xs font-bold tracking-[0.2em] uppercase" style={{ color: 'rgba(255,255,255,0.2)' }}>
              / 02
            </span>
          </div>

          <div className="flex gap-1.5 mb-8">
            <div className="h-0.5 flex-1 rounded-full" style={{ background: '#F97316' }} />
            <div className="h-0.5 flex-1 rounded-full" style={{ background: step === 2 ? '#F97316' : 'rgba(255,255,255,0.12)' }} />
          </div>

          <h2 className="text-2xl font-bold mb-6" style={{ color: '#F1F5F9' }}>
            {step === 1 ? t('login.emailStepTitle') : t('login.passwordStepTitle')}
          </h2>

          {reason === 'session-expired' && (
            <div
              className="mb-5 p-3.5 rounded-xl text-sm flex items-start gap-2.5"
              style={{ background: 'rgba(245,158,11,0.1)', border: '1px solid rgba(245,158,11,0.25)', color: '#FDE68A' }}
            >
              <svg className="w-4 h-4 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4m0 4h.01M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0z" />
              </svg>
              {t('login.sessionExpired')}
            </div>
          )}

          {error && (
            <div
              className="mb-5 p-3.5 rounded-xl text-sm flex items-start gap-2.5"
              style={{ background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.2)', color: '#FCA5A5' }}
            >
              <svg className="w-4 h-4 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4m0 4h.01M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0z" />
              </svg>
              <div className="flex-1 min-w-0">
                <div>{error}</div>
                {isRateLimited && (
                  <div className="mt-1 text-xs" style={{ color: '#FECACA' }}>
                    {t('login.tryAgainIn', { value: retryAfterLabel })}
                  </div>
                )}
                {errorInfo?.isLockedOut && errorInfo.lockoutContactMessage && (
                  <div className="mt-2 pt-2 text-xs border-t" style={{ color: '#FECACA', borderColor: 'rgba(239,68,68,0.2)' }}>
                    {errorInfo.lockoutContactMessage}
                    {errorInfo.lockoutContactPhone && (
                      <div className="mt-1">
                        <span style={{ opacity: 0.7 }}>☎ </span>
                        <a
                          href={`tel:${errorInfo.lockoutContactPhone}`}
                          className="underline hover:no-underline"
                          style={{ color: '#FCA5A5' }}
                        >
                          {errorInfo.lockoutContactPhone}
                        </a>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}

          {showSlowLoginNotice && (
            <div
              className="mb-5 p-3.5 rounded-xl text-sm flex items-start gap-2.5"
              style={{ background: 'rgba(249,115,22,0.1)', border: '1px solid rgba(249,115,22,0.2)', color: '#FDBA74' }}
            >
              <svg className="w-4 h-4 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6l4 2m6-2a10 10 0 1 1-20 0 10 10 0 0 1 20 0z" />
              </svg>
              {t('login.slowNotice')}
            </div>
          )}

          <form onSubmit={handleContinue} className="space-y-4">
            <div className="relative">
              <span className="absolute inset-y-0 left-4 flex items-center pointer-events-none">
                <span className="w-2 h-2 rounded-full" style={{ background: '#F97316' }} />
              </span>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full pl-9 pr-4 py-3.5 rounded-xl text-sm font-medium focus:outline-none transition-[color,background-color,border-color,box-shadow] duration-[var(--motion-state)] motion-reduce:transition-none"
                style={{
                  background: '#0D1F2D',
                  border: '1.5px solid #F97316',
                  color: '#F1F5F9',
                  boxShadow: '0 0 0 0px #F97316',
                }}
                onFocus={(e) => { e.currentTarget.style.boxShadow = '0 0 0 3px rgba(249,115,22,0.15)'; }}
                onBlur={(e) => { e.currentTarget.style.boxShadow = 'none'; }}
                placeholder={t('login.emailPlaceholder')}
                required
                autoComplete="email"
                readOnly={step === 2}
              />
            </div>

            {step === 2 && (
              <div className="relative">
                <span className="absolute inset-y-0 left-4 flex items-center pointer-events-none">
                  <span className="w-2 h-2 rounded-full" style={{ background: '#F97316' }} />
                </span>
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full pl-9 pr-12 py-3.5 rounded-xl text-sm font-medium focus:outline-none transition-[color,background-color,border-color,box-shadow] duration-[var(--motion-state)] motion-reduce:transition-none"
                  style={{
                    background: '#0D1F2D',
                    border: '1.5px solid #1B3142',
                    color: '#F1F5F9',
                  }}
                  onFocus={(e) => {
                    e.currentTarget.style.border = '1.5px solid #F97316';
                    e.currentTarget.style.boxShadow = '0 0 0 3px rgba(249,115,22,0.15)';
                  }}
                  onBlur={(e) => {
                    e.currentTarget.style.border = '1.5px solid #1B3142';
                    e.currentTarget.style.boxShadow = 'none';
                  }}
                  placeholder={t('login.passwordPlaceholder')}
                  required
                  autoComplete="current-password"
                  autoFocus
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((value) => !value)}
                  className="absolute inset-y-0 right-0 pr-4 flex items-center"
                  style={{ color: 'rgba(255,255,255,0.3)' }}
                  tabIndex={-1}
                >
                  {showPassword ? (
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.75}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M13.875 18.825A10.05 10.05 0 0 1 12 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 0 1 1.563-3.029m5.858.908a3 3 0 1 1 4.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532 3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0 1 12 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 0 1-4.132 5.411m0 0L21 21" />
                    </svg>
                  ) : (
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.75}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                    </svg>
                  )}
                </button>
              </div>
            )}

            {step === 2 && (
              <button
                type="button"
                onClick={() => setStep(1)}
                className="text-xs font-medium transition-colors"
                style={{ color: 'rgba(255,255,255,0.3)' }}
                onMouseEnter={(e) => { e.currentTarget.style.color = 'rgba(255,255,255,0.6)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.color = 'rgba(255,255,255,0.3)'; }}
              >
                {'<-'} {t('login.changeEmail')}
              </button>
            )}

            <button
              type="submit"
              disabled={loading || isRateLimited}
              className="w-full py-3.5 rounded-xl font-bold text-sm tracking-wide disabled:opacity-60 transition-colors flex items-center justify-center gap-2 mt-2"
              style={{ background: '#F97316', color: '#0D1F2D' }}
              onMouseEnter={(e) => { if (!loading) e.currentTarget.style.background = '#ea6c10'; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = '#F97316'; }}
            >
              {loading ? (
                <>
                  <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 0 1 8-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                  {t('login.signingIn')}
                </>
              ) : isRateLimited ? (
                <>{t('login.tryAgainIn', { value: retryAfterLabel })}</>
              ) : (
                <>{t('login.continue')} {'->'}</>
              )}
            </button>

            {(statusCardStyle || helperCopy || step === 2) && (
              <div
                className="rounded-2xl px-4 py-3 text-xs"
                style={statusCardStyle || { background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)', color: 'rgba(255,255,255,0.52)' }}
              >
                <div className="flex items-center justify-between gap-3">
                  <span className="font-semibold tracking-[0.14em] uppercase" style={{ color: isRateLimited ? '#FCA5A5' : showSlowLoginNotice ? '#FDBA74' : 'rgba(255,255,255,0.38)' }}>
                    {isRateLimited ? t('login.locked') : showSlowLoginNotice ? t('login.connecting') : t('login.session')}
                  </span>
                  {isRateLimited && (
                    <span className="rounded-full px-2.5 py-1 font-bold" style={{ background: 'rgba(248,113,113,0.14)', color: '#FECACA' }}>
                      {retryAfterLabel}
                    </span>
                  )}
                </div>
                <p className="mt-2 leading-relaxed">
                  {helperCopy || t('login.waitAndRetry')}
                </p>
              </div>
            )}
          </form>
        </div>
      </div>

      <div className="lg:hidden absolute inset-0 z-20 flex flex-col p-6" style={{ background: '#07131C' }}>
        <div className="flex items-center gap-3 mb-10">
          <div className="w-8 h-8 flex-shrink-0">
            <svg viewBox="0 0 200 200" xmlns="http://www.w3.org/2000/svg">
              <g transform="translate(100 100) rotate(45)"><rect x="-86" y="-22" width="172" height="44" rx="4" fill="#F97316" /></g>
              <g transform="translate(100 100) rotate(-45)"><rect x="-86" y="-22" width="172" height="44" rx="4" fill="#F97316" /></g>
              <circle cx="100" cy="100" r="5" fill="#07131C" />
              <circle cx="100" cy="100" r="2" fill="#F97316" />
            </svg>
          </div>
          <span className="text-white font-bold text-lg tracking-[0.14em]">
            FOR<span style={{ color: '#F97316' }}>X</span>A
          </span>
        </div>

        <div className="flex-1 flex flex-col justify-center max-w-sm w-full mx-auto">
          <div className="flex items-center gap-1.5 mb-4">
            <span className="text-xs font-bold tracking-[0.2em] uppercase" style={{ color: '#F97316' }}>
              {'// '}{t('login.step')} {step === 1 ? '01' : '02'}
            </span>
            <span className="text-xs font-bold tracking-[0.2em] uppercase" style={{ color: 'rgba(255,255,255,0.2)' }}>
              / 02
            </span>
          </div>
          <div className="flex gap-1.5 mb-6">
            <div className="h-0.5 flex-1 rounded-full" style={{ background: '#F97316' }} />
            <div className="h-0.5 flex-1 rounded-full" style={{ background: step === 2 ? '#F97316' : 'rgba(255,255,255,0.12)' }} />
          </div>
          <h2 className="text-2xl font-bold mb-6" style={{ color: '#F1F5F9' }}>
            {step === 1 ? t('login.emailStepTitle') : t('login.passwordStepTitle')}
          </h2>

          {reason === 'session-expired' && (
            <div
              className="mb-4 p-3.5 rounded-xl text-sm flex items-start gap-2.5"
              style={{ background: 'rgba(245,158,11,0.1)', border: '1px solid rgba(245,158,11,0.25)', color: '#FDE68A' }}
            >
              {t('login.sessionExpired')}
            </div>
          )}

          {error && (
            <div
              className="mb-4 p-3.5 rounded-xl text-sm"
              style={{ background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.2)', color: '#FCA5A5' }}
            >
              <div>{error}</div>
              {isRateLimited && (
                <div className="mt-1 text-xs" style={{ color: '#FECACA' }}>
                  {t('login.tryAgainIn', { value: retryAfterLabel })}
                </div>
              )}
            </div>
          )}

          {showSlowLoginNotice && (
            <div
              className="mb-4 p-3.5 rounded-xl text-sm"
              style={{ background: 'rgba(249,115,22,0.1)', border: '1px solid rgba(249,115,22,0.2)', color: '#FDBA74' }}
            >
              {t('login.slowNotice')}
            </div>
          )}

          <form onSubmit={handleContinue} className="space-y-4">
            <div className="relative">
              <span className="absolute inset-y-0 left-4 flex items-center pointer-events-none">
                <span className="w-2 h-2 rounded-full" style={{ background: '#F97316' }} />
              </span>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full pl-9 pr-4 py-3.5 rounded-xl text-sm font-medium focus:outline-none"
                style={{ background: '#0D1F2D', border: '1.5px solid #F97316', color: '#F1F5F9' }}
                placeholder={t('login.emailPlaceholder')}
                required
                autoComplete="email"
                readOnly={step === 2}
              />
            </div>

            {step === 2 && (
              <div className="relative">
                <span className="absolute inset-y-0 left-4 flex items-center pointer-events-none">
                  <span className="w-2 h-2 rounded-full" style={{ background: '#F97316' }} />
                </span>
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full pl-9 pr-4 py-3.5 rounded-xl text-sm font-medium focus:outline-none"
                  style={{ background: '#0D1F2D', border: '1.5px solid #1B3142', color: '#F1F5F9' }}
                  placeholder={t('login.passwordPlaceholder')}
                  required
                  autoComplete="current-password"
                  autoFocus
                />
              </div>
            )}

            {step === 2 && (
              <button type="button" onClick={() => setStep(1)} className="text-xs" style={{ color: 'rgba(255,255,255,0.3)' }}>
                {'<-'} {t('login.changeEmail')}
              </button>
            )}

            <button
              type="submit"
              disabled={loading || isRateLimited}
              className="w-full py-3.5 rounded-xl font-bold text-sm disabled:opacity-60 flex items-center justify-center gap-2"
              style={{ background: '#F97316', color: '#0D1F2D' }}
            >
              {loading
                ? t('login.signingIn')
                : isRateLimited
                ? t('login.tryAgainIn', { value: retryAfterLabel })
                : `${t('login.continue')} ->`}
            </button>

            {(statusCardStyle || helperCopy || step === 2) && (
              <div
                className="rounded-2xl px-4 py-3 text-xs"
                style={statusCardStyle || { background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)', color: 'rgba(255,255,255,0.52)' }}
              >
                <div className="flex items-center justify-between gap-3">
                  <span className="font-semibold tracking-[0.14em] uppercase" style={{ color: isRateLimited ? '#FCA5A5' : showSlowLoginNotice ? '#FDBA74' : 'rgba(255,255,255,0.38)' }}>
                    {isRateLimited ? t('login.locked') : showSlowLoginNotice ? t('login.connecting') : t('login.session')}
                  </span>
                  {isRateLimited && (
                    <span className="rounded-full px-2 py-0.5 font-bold" style={{ background: 'rgba(248,113,113,0.14)', color: '#FECACA' }}>
                      {retryAfterLabel}
                    </span>
                  )}
                </div>
                <p className="mt-2 leading-relaxed">
                  {helperCopy || t('login.waitAndRetryShort')}
                </p>
              </div>
            )}
          </form>
        </div>
      </div>
    </div>
  );
}
